import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretRightOutlined } from '@ant-design/icons';
import { Collapse, Spin } from 'antd';
import { cloneDeep } from 'lodash';
import HeaderDetails from './HeaderDetails';
import RiskIndicatorTable from './RiskIndicatorTable';
import styled from 'styled-components';
import CopyComponent from './CopyComponent';

const { Panel } = Collapse;

const RiskIndicators = ({
  typologies,
  riskIndicators,
  handleUserThreshold,
  handleCopyRI,
  handleActivePanel,
  isSaved
}) => {
  const [isPanelLoading, setPanelLoading] = useState([]);
  const [selectedCombination, setCombination] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const handleDropdown = async (panelIndex, typologyId, combinationId) => {
    let selectedCombinations = cloneDeep(selectedCombination);
    selectedCombinations[panelIndex] = combinationId;
    setCombination(selectedCombinations);

    let ri = riskIndicators?.find(
      (ri) => ri?.id === typologyId && ri?.segment?.id === combinationId
    );
    if (!ri?.derivedIndicators) {
      handlePanelChange(panelIndex);
    }
  };

  useEffect(() => {
    if (
      typologies?.[activeTab ?? 0]?.typology?.id &&
      typologies?.[activeTab ?? 0]?.combinations?.length
    ) {
      let activeLoader = isPanelLoading;
      activeLoader.push(typologies?.[activeTab ?? 0]?.typology?.id);
      setPanelLoading(activeLoader);
    }
  }, [typologies, isSaved]);

  useMemo(() => {
    let activeLoader = isPanelLoading;
    let loadedRI = riskIndicators.map(({ id }) => id);
    activeLoader = activeLoader.filter((id) => !loadedRI.includes(id));
    setPanelLoading(activeLoader);
  }, [riskIndicators]);

  const getRiskIndicators = (panelIndex, typology, combinations) => {
    let selectedCombinationId = getSelectedCombinationID(
      panelIndex,
      combinations
    );
    if (typology?.id && riskIndicators?.length) {
      let ri = riskIndicators?.find((ri) =>
        selectedCombinationId
          ? ri?.id === typology?.id && ri?.segment?.id === selectedCombinationId
          : ri?.id === typology?.id
      );
      return ri?.derivedIndicators ?? [];
    }

    return [];
  };

  const getSelectedCombinationID = (panelIndex, combinations) => {
    return selectedCombination[panelIndex] ?? combinations?.[0]?.id;
  };

  const handlePanelChange = (index) => {
    setActiveTab(index ?? 0);
    let typology = typologies?.[index];
    let typologyId = typology?.typology?.id;
    let combinationId = getSelectedCombinationID(index, typology?.combinations);
    let ri = riskIndicators?.find(
      (ri) => ri?.id === typologyId && ri?.segment?.id === combinationId
    );
    if (!ri?.derivedIndicators && typology?.combinations?.length) {
      let activeLoader = isPanelLoading;
      activeLoader.push(typologyId);
      setPanelLoading(activeLoader);
      handleActivePanel(index);
    }
  };

  useMemo(() => {
    if (isSaved && riskIndicators?.length === 0) handlePanelChange(activeTab);
  }, [isSaved, riskIndicators]);

  return (
    <div>
      <StyledCollapse
        bordered={false}
        defaultActiveKey={['0']}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
        accordion
        onChange={handlePanelChange}>
        {typologies.map(({ typology, combinations }, index) => {
          return (
            <Panel
              header={
                <HeaderDetails
                  typology={typology}
                  combinations={combinations}
                  handleDropdown={handleDropdown}
                  panelIndex={index}
                />
              }
              key={index}>
              <Spin spinning={isPanelLoading.includes(typology?.id)}>
                <RiskIndicatorTable
                  riskIndicators={getRiskIndicators(
                    index,
                    typology,
                    combinations
                  )}
                  handleUserThreshold={handleUserThreshold}
                  typologyId={typology?.id}
                  selectedCombinationId={getSelectedCombinationID(
                    index,
                    combinations
                  )}
                />
                <CopyComponent
                  combinations={combinations}
                  selectedCombinationId={getSelectedCombinationID(
                    index,
                    combinations
                  )}
                  typology={typology}
                  handleCopyRI={handleCopyRI}
                  isSaved={isSaved}
                />
              </Spin>
            </Panel>
          );
        })}
      </StyledCollapse>
    </div>
  );
};

RiskIndicators.propTypes = {
  typologies: PropTypes.array,
  riskIndicators: PropTypes.array,
  handleUserThreshold: PropTypes.func,
  handleCopyRI: PropTypes.func,
  handleActivePanel: PropTypes.func,
  isSaved: PropTypes.bool
};

const StyledCollapse = styled(Collapse)`
  padding: 20px;
  .ant-collapse-item {
    margin-bottom: 10px;
    border: 1px solid #d0d7e3;
    border-radius: 10px !important;

    .ant-collapse-header {
      display: flex;
      align-items: baseline;
      background-color: #f7f7f8;
    }
  }
`;

export default RiskIndicators;
